// src/features/feeds/components/FeedDetailPage.tsx

import { useState, useCallback, useEffect } from 'react';
import {
  Stack, Group, Title, Text, Button, Loader, Alert, Box, Badge, Anchor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from '@tanstack/react-router';
import { IconAlertCircle, IconArrowLeft, IconCode } from '@tabler/icons-react';
import { useFeed } from '../useFeed';
import { useUpdateFeed } from '../useFeedMutations';
import { FeedUtils } from '../FeedUtils';
import { feedFormSchema } from '../schemas/FeedForm.schema';
import { FeedGeneralInfoForm } from './FeedGeneralInfoForm';
import { FeedSourcesSection } from './FeedSourcesSection';
import { FeedPublishModal } from './FeedPublishModal';
import type { FeedFormData, FeedSourceData } from '@/lib/types/Feed';

export function FeedDetailPage() {
  const { id: displayId } = useParams({ strict: false });
  const feedId = FeedUtils.getNumericId(displayId!);
  const navigate = useNavigate();

  const { data: feed, isLoading, isError, error } = useFeed(feedId);
  const updateMutation = useUpdateFeed(feedId);

  const [localSources, setLocalSources] = useState<FeedSourceData[]>([]);
  const [sourcesInitialized, setSourcesInitialized] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  // Initialize form with feed data
  const { control, handleSubmit, reset, watch, formState: { isDirty } } = useForm<FeedFormData>({
    resolver: zodResolver(feedFormSchema),
    defaultValues: {
      title: '',
      descShort: '',
      mediaStateType: 'active',
      language: 'en',
      itunesExplicit: 'no',
    },
  });

  // Sync form and sources when feed data loads
  useEffect(() => {
    if (feed) {
      const formData = FeedUtils.transformFeedToFormData(feed);
      reset(formData);
      if (!sourcesInitialized) {
        setLocalSources([...feed.sources].sort((a, b) => a.position - b.position));
        setSourcesInitialized(true);
      }
    }
  }, [feed, reset, sourcesInitialized]);

  // Track if sources have changed from the original
  const sourcesChanged = sourcesInitialized && feed
    ? JSON.stringify(localSources.map(s => ({ listId: s.listId, title: s.title })))
      !== JSON.stringify(feed.sources.sort((a, b) => a.position - b.position).map(s => ({ listId: s.listId, title: s.title })))
    : false;

  const hasChanges = isDirty || sourcesChanged;

  const handleSave = handleSubmit((data: FeedFormData) => {
    const req = FeedUtils.transformFormToUpdateRequest(data, localSources);
    updateMutation.mutate(req, {
      onSuccess: (updated) => {
        notifications.show({ title: 'Saved', message: 'Feed updated successfully.', color: 'green' });
        // Reset sources tracking after save
        setLocalSources([...updated.sources].sort((a, b) => a.position - b.position));
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  });

  const handleDiscard = useCallback(() => {
    if (feed) {
      reset(FeedUtils.transformFeedToFormData(feed));
      setLocalSources([...feed.sources].sort((a, b) => a.position - b.position));
    }
  }, [feed, reset]);

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading feed...</Text>
      </Stack>
    );
  }

  if (isError || !feed) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load feed'}
      </Alert>
    );
  }

  const feedDisplayId = FeedUtils.getDisplayId(feed.feedId);
  const isActive = feed.mediaStateType === 'active';

  return (
    <Stack gap="md">
      {/* Header */}
      <Box>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/feeds' })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Feeds
        </Anchor>
      </Box>

      <Group justify="space-between" align="flex-start">
        <Box>
          <Title order={2}>{feed.title}</Title>
          <Group gap="xs" mt={4}>
            <Text size="sm" c="dimmed" ff="monospace">{feedDisplayId}</Text>
            <Badge
              variant="light"
              color={isActive ? 'green' : 'gray'}
              size="sm"
            >
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </Group>
        </Box>
        <Button
          leftSection={<IconCode size={16} />}
          variant="light"
          onClick={() => setPublishOpen(true)}
          disabled={!isActive}
        >
          Publish
        </Button>
      </Group>

      {/* Form with sticky save bar */}
      <form onSubmit={handleSave}>
        <Box
          pos="sticky"
          top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
          bg="white"
          py="sm"
          style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group justify="space-between">
            {hasChanges ? (
              <Text size="sm" c="orange" fw={500}>Unsaved changes</Text>
            ) : (
              <div />
            )}
            <Group gap="sm">
              <Button
                variant="default"
                onClick={handleDiscard}
                disabled={!hasChanges}
              >
                Discard
              </Button>
              <Button
                type="submit"
                disabled={!hasChanges}
                loading={updateMutation.isPending}
              >
                Save Changes
              </Button>
            </Group>
          </Group>
        </Box>

        <Box mt="md">
          <Text fw={600} size="lg" mb="md">General Information</Text>
          <FeedGeneralInfoForm control={control} watch={watch} />
        </Box>
      </form>

      {/* Sources Section */}
      <Box mt="xl">
        <FeedSourcesSection
          sources={localSources}
          onSourcesChange={setLocalSources}
          feedId={feed.feedId}
        />
      </Box>

      {/* Publish Modal */}
      {publishOpen && (
        <FeedPublishModal
          opened={publishOpen}
          onClose={() => setPublishOpen(false)}
          feed={feed}
        />
      )}
    </Stack>
  );
}
